import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';
import { AppSettings } from 'src/app/app.settings';
import { BaseResponse } from 'src/app/interfaces/base-response-interface';
import { VendorProduct } from '../vendorproducts/vendorproducts.service';

export interface IProduct {
  id: string;
  stripeProductId: string;
  name: string;
  description: string;
  metadata?: Record<string, unknown>;
  billingCycleId: number;
  billingDay?: number;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IBillingCycle {
  id: number;
  type: string;
  proRate: boolean;
  delayedStart: boolean;
  splitCycle: boolean;
  annual: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IBrandBillingCycle {
  brandId: string;
  productId: string;
  billingCycleId: number;
  billingDay: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export const BILLING_CYCLE_LABELS = {
  variableBilling: 'Variable Billing',
  fixedBilling: 'Fixed Billing',
  prorateBilling: 'Prorate Billing',
  advancedPayment: 'Advanced Payment',
  annualVariableBilling: 'Annual Variable Billing',
  useBeforePay: 'Use Before Pay',
};

export type VendorProductWithSubprojectProduct = VendorProduct & { subprojectProductId?: string };

@Injectable({
  providedIn: 'root',
})
export class ProductsSubprojectService {
  constructor(private baseRestAngular: ApiClient, private restAngular: ApiClient) {
    this.restAngular = this.getCustomRestAngular();
  }

  getCustomRestAngular() {
    return this.baseRestAngular.withBaseUrl(AppSettings.getProductsEndpoint());
  }
  /**
   * Fetches all active products from the Stripe using products API.
   * @returns Promise<IProduct[]> - A promise that resolves to an array of products.
   */
  getActiveProducts(): Promise<
    { success: true; data: IProduct[] } | { success: false; error: any }
  > {
    return this.restAngular.one('products').customGET('', { onlyActive: true }).toPromise();
  }

  getProductByStripeProductId(stripeProductId: string): Promise<BaseResponse<IProduct[]>> {
    return this.restAngular
      .one('products', 'find')
      .customPOST({ ids: [stripeProductId] })
      .toPromise();
  }

  getBillingCycles(): Promise<BaseResponse<IBillingCycle[]>> {
    return this.restAngular.one('billing-cycles').customGET().toPromise();
  }

  getBrandsBillingCycleByProductId(productId: string): Promise<BaseResponse<IBrandBillingCycle[]>> {
    return this.restAngular
      .one('brands')
      .one('billing-cycle')
      .one('product', productId)
      .customGET()
      .toPromise();
  }

  getBrandsBillingCycleByBrandId(brandId: string): Promise<BaseResponse<IBrandBillingCycle[]>> {
    return this.restAngular
      .one('brands')
      .one('billing-cycle')
      .one('brand', brandId)
      .customGET()
      .toPromise();
  }

  updateProduct(
    productId: string,
    productData: Partial<IProduct>,
  ): Promise<BaseResponse<IProduct>> {
    return this.restAngular.one('products', productId).customPUT(productData).toPromise();
  }

  createOrUpdateBrandBillingCycle(
    productId: string,
    brandId: string,
    billingCycleId: number,
    billingDay?: number,
  ): Promise<BaseResponse<IBrandBillingCycle>> {
    return this.restAngular
      .one('brands')
      .one('billing-cycle')
      .customPOST({ productId, brandId, billingCycleId, billingDay })
      .toPromise();
  }

  deleteBrandBillingCycle(productId: string, brandId: string): Promise<BaseResponse<null>> {
    return this.restAngular
      .one('brands', brandId)
      .one('products', productId)
      .one('billing-cycle')
      .customDELETE()
      .toPromise();
  }
}
